// src/pages/OrderConfirmation/OrderConfirmation.jsx
// FRONT OFFICE - Order confirmation page with Firebase integration and WhatsApp integration fixes

import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FaCheckCircle, FaBoxOpen, FaShippingFast, FaPrint, FaWhatsapp } from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import { getOrderById } from '../../services/OrderService';
import './OrderConfirmation.css';

const OrderConfirmation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Extract order ID from location state or URL parameter
  const orderId = location.state?.orderId || new URLSearchParams(location.search).get('id');
  
  useEffect(() => {
    // Redirect to home if no order ID is provided
    if (!orderId) {
      navigate('/');
      return;
    }
    
    const fetchOrderDetails = async () => {
      try {
        setLoading(true);
        const orderData = await getOrderById(orderId);
        
        // Verify the order belongs to the current user
        if (orderData.userId !== currentUser?.uid) {
          setError('You do not have permission to view this order.');
          navigate('/my-account');
          return;
        }
        
        setOrder(orderData);
      } catch (err) {
        console.error('Error fetching order:', err);
        setError('Failed to load order details. Please check your order history.');
      } finally {
        setLoading(false);
      }
    };
    
    if (currentUser) {
      fetchOrderDetails();
    }
  }, [orderId, currentUser, navigate]);
  
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    
    const date = new Date(timestamp.seconds * 1000);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };
  
  const handlePrintOrder = () => {
    window.print();
  };
  
  const handleContactSupport = () => {
    // Create WhatsApp message for support
    const message = `Hello, I have a question about my order ${orderId} placed on ${formatDate(order?.createdAt)}.`;
    
    // Replace with your actual WhatsApp support number
    const whatsappNumber = '+491632055734';
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
    
    window.open(whatsappUrl, '_blank');
  };
  
  const handleCompletePayment = () => {
    // Create payment completion message with order details
    const itemsList = order?.items.map(item => 
      `${item.product?.name} (${item.quantity} × €${item.price?.toFixed(2) || '0.00'})`
    ).join("\n- ");
    
    const message = `Hello, I would like to complete payment for my order ${orderId} totaling €${order?.total.toFixed(2)}.\n\nOrder Details:\n- ${itemsList}\n\nShipping Address: ${order?.shippingAddress}`;
    
    // WhatsApp number
    const whatsappNumber = '+491632055734';
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
    
    window.open(whatsappUrl, '_blank');
  };
  
  if (loading) {
    return (
      <div className="order-confirmation-page">
        <div className="container">
          <div className="loading-indicator">
            <div className="spinner"></div>
            <p>Loading order details...</p>
          </div>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="order-confirmation-page">
        <div className="container">
          <div className="error-message">
            <h2>Error</h2>
            <p>{error}</p>
            <Link to="/my-account" className="btn btn-primary">Go to My Account</Link>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="order-confirmation-page">
      <div className="container">
        <div className="confirmation-header">
          <div className="success-icon">
            <FaCheckCircle />
          </div>
          <h1>Order Confirmed!</h1>
          <p>Your order has been placed successfully.</p>
        </div>
        
        <div className="order-summary">
          <div className="order-summary-header">
            <div>
              <h2>Order Summary</h2>
              <p className="order-id">Order {orderId}</p>
              <p className="order-date">Placed on {formatDate(order?.createdAt)}</p>
            </div>
            <div className="order-status">
              <span className={`status-badge ${order?.status.toLowerCase()}`}>
                {order?.status}
              </span>
            </div>
          </div>
          
          <div className="order-details">
            <div className="order-items">
              <h3><FaBoxOpen /> Items Ordered</h3>
              <div className="items-list">
                {order?.items && order.items.map((item, index) => (
                  <div key={index} className="order-item">
                    <div className="item-image">
                      <img 
                        src={item.product?.image || "https://via.placeholder.com/60x60?text=Product"} 
                        alt={item.product?.name} 
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "https://via.placeholder.com/60x60?text=Product";
                        }}
                      />
                    </div>
                    <div className="item-details">
                      <h4>{item.product?.name}</h4>
                      <p>Qty: {item.quantity}</p>
                    </div>
                    <div className="item-price">
                      €{(item.price * item.quantity).toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="order-shipping">
              <h3><FaShippingFast /> Shipping Information</h3>
              <div className="shipping-details">
                <p><strong>Shipping Address:</strong></p>
                <p>{order?.shippingAddress}</p>
                
                <p className="mt-3"><strong>Shipping Method:</strong></p>
                <p>Standard Shipping</p>
                
                <p className="mt-3"><strong>Estimated Delivery:</strong></p>
                <p>3-5 Business Days</p>
              </div>
            </div>
          </div>
          
          <div className="order-totals">
            <div className="totals-row">
              <span>Subtotal:</span>
              <span>€{order?.subtotal.toFixed(2)}</span>
            </div>
            <div className="totals-row">
              <span>Shipping:</span>
              <span>{order?.shippingCost > 0 ? `€${order.shippingCost.toFixed(2)}` : 'FREE'}</span>
            </div>
            <div className="totals-row grand-total">
              <span>Total:</span>
              <span>€{order?.total.toFixed(2)}</span>
            </div>
          </div>
          
          <div className="payment-info">
            <h3>Payment Information</h3>
            <p><strong>Payment Method:</strong> {order?.paymentMethod || 'WhatsApp'}</p>
            <p><strong>Payment Status:</strong> {order?.paymentStatus || 'Pending'}</p>
            
            {(order?.paymentStatus === 'pending' || !order?.paymentStatus) && (
              <div className="payment-instructions">
                <p>Please complete your payment by contacting us via WhatsApp. Just click the button below!</p>
                <button 
                  className="whatsapp-btn"
                  onClick={handleCompletePayment}
                >
                  <FaWhatsapp /> Complete Payment via WhatsApp
                </button>
              </div>
            )}
          </div>
        </div>
        
        <div className="confirmation-actions">
          <button className="btn btn-outlined" onClick={handlePrintOrder}>
            <FaPrint /> Print Order
          </button>
          <button className="btn btn-outlined" onClick={handleContactSupport}>
            <FaWhatsapp /> Contact Support
          </button>
          <Link to="/shop" className="btn btn-primary">
            Continue Shopping
          </Link>
        </div>
        
        <div className="confirmation-footer">
          <p>
            Thank you for your order! If you have any questions, please{' '}
            <button className="text-link" onClick={handleContactSupport}>contact our support team</button>.
          </p>
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmation;



